import { useEffect, useRef } from 'react';

import { type ElementOrRef, resolveElementOrRef } from '@/util/element';

import useFlag from './useFlag';

const useIntersectionObserver = <T extends HTMLElement>(
  elementRef: React.RefObject<Nullish<T>>,
  callback: (entries: IntersectionObserverEntry[]) => void,
  {
    threshold = 0,
    root = null,
    rootMargin = '0px',
  }: Omit<IntersectionObserverInit, 'root'> & { root?: ElementOrRef },
) => {
  const { value: isActive, setFalse: pause, setTrue: resume } = useFlag(true);
  const cleanup = useRef(() => {});
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  const stop = () => {
    cleanup.current();
    pause();
  };

  useEffect(() => {
    cleanup.current();

    const node = elementRef?.current;
    const rootElement = resolveElementOrRef(root);
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || !node || !isActive) return;

    const observer = new IntersectionObserver(
      entries => callbackRef.current(entries),
      {
        root: rootElement,
        rootMargin,
        threshold,
      },
    );

    observer.observe(node);

    cleanup.current = () => {
      observer.disconnect();
      cleanup.current = () => {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    elementRef,
    isActive,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(threshold),
    root,
    rootMargin,
  ]);

  useEffect(() => {
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isActive,
    pause,
    resume,
    stop,
  };
};

export default useIntersectionObserver;
